import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChooseListInput } from '@/shared/components/inputs/ChooseList.input';
import { WizardStepProps } from '../types/wizard.types';
import { ComponentReturnTypeMap } from '@momentum/types';

export interface CompanyValueOption {
  id: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
}

interface WizardCompanyValuesChoiceStepProps
  extends WizardStepProps<ComponentReturnTypeMap['CompanyValuesChoice']> {
  companyValues: CompanyValueOption[];
  columns?: 1 | 2 | 3;
}

export const WizardCompanyValuesChoiceStep: React.FC<
  WizardCompanyValuesChoiceStepProps
> = ({
  value,
  onChange,
  onNext,
  onPrevious: _onPrevious,
  error,
  disabled,
  companyValues,
  columns = 2,
  required = false,
}) => {
  const { t } = useTranslation('wizard');
  const handleChange = (selectedValueId: string[]) => {
    onChange(selectedValueId);
    // Automatically proceed to next step when a value is selected
    if (selectedValueId && onNext) {
      setTimeout(() => {
        onNext(selectedValueId);
      }, 100); // Small delay for better UX
    }
  };

  // Add default "no effect" option
  const defaultOption: CompanyValueOption = {
    id: 'no-effect',
    title: t('common.companyValuesChoice.noEffect.title'),
    icon: (
      <svg
        className="w-6 h-6 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"
        />
      </svg>
    ),
  };

  const allValues =
    companyValues.length > 0
      ? [...companyValues, defaultOption]
      : [defaultOption];

  return (
    <div className="w-full mx-auto">
      <ChooseListInput
        options={allValues}
        value={value || []}
        onChange={handleChange}
        variant="card"
        columns={columns}
        title=""
        description=""
        required={required}
        disabled={disabled}
        className="w-full"
        multiSelect={true}
      />

      {error && (
        <p className="mt-4 text-sm text-red-600 text-center">{error}</p>
      )}
    </div>
  );
};

/* 
MongoDB Documents that should use this component:
- All questions with type: "company-values-choice" (14 documents)
- Examples: 
  - "Does recipientPlaceholder's praise contribute to the reinforcement of company values?"
  - "Does recipientPlaceholder meeting expectations contribute to the reinforcement of company values?"
  - Questions asking if an interaction reinforced specific company values
  - Always includes "No, does not affect company values" as an option
*/
